import './grid_section_desktop_layout.less';

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import $ from 'jquery';

import get from 'lodash/get';
import maxBy from 'lodash/maxBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import hasEqualProps from 'zoomforth_zfutils/has_equal_props.js';

import {
  DESKTOP_TILE_MARGIN_FACTOR,
  DESKTOP_TILE_PADDING_FACTOR
} from 'zoomforth_codesbane_viewer/lib/aspect_ratios.js';

import GridLines from 'zoomforth_codesbane_editor/in_page/section_editors/grid/grid_lines.jsx';
import GridSectionFilters from 'zoomforth_codesbane_viewer/viewer/sections/grid/grid_section_filters_container.jsx';

/* MEASUREMENTS DONE AFTER WIDTH IS DETERMINED: */

const getTileLength = (internalGridWidth, columnCount) => {
  return parseInt(
    internalGridWidth / (1.0 * columnCount)
  );
};

const getContainerHeight = (tileLength, highestRow) => {
  return tileLength * highestRow;
};

const getTileMargin = (tileLength, isSectionFullWidth, tileSpacingStyle) => {
  if (!isNil(tileSpacingStyle)) {
    return tileSpacingStyle;
  }

  if (isSectionFullWidth) {
    return '0px';
  }

  return `${DESKTOP_TILE_MARGIN_FACTOR * tileLength}px`;
};

const getTilePadding = (tileLength, isSectionFullWidth, tileSpacingStyle) => {
  if (!isNil(tileSpacingStyle)) {
    return tileSpacingStyle;
  }

  if (isSectionFullWidth) {
    return '0px';
  }

  return `${DESKTOP_TILE_PADDING_FACTOR * tileLength}px`;
};

const getHighestRow = (gridPositions, spacingRowsAtBottomCount) => {
  // gridPositions is an array of objects that each must
  // contain a row and sizey attribute
  const highestRowTile = maxBy(gridPositions, (tile)=>{
    return tile.row + tile.sizey - 1;
  });

  if (!highestRowTile) {
    return spacingRowsAtBottomCount;
  }

  const highestRow = highestRowTile.row + highestRowTile.sizey - 1;

  return highestRow + spacingRowsAtBottomCount;
};


class GridSectionDesktopLayout extends Component {

  constructor(props) {
    super(props);
    this.containerRef = null;
    this.lastMeasuredWidth = null;
  }

  componentDidMount() {
    // Force update after the ref is set so we can measure the actual width
    if (this.containerRef) {
      this.lastMeasuredWidth = $(this.containerRef).width();
      this.forceUpdate();
    }
  }

  componentDidUpdate(prevProps) {
    // After DOM updates, check if the container's actual width has changed
    // This happens when tab margins/padding change - props update first, then DOM updates
    if (this.containerRef) {
      // Use requestAnimationFrame to ensure the browser has painted the new styles
      requestAnimationFrame(() => {
        if (this.containerRef) {
          const currentWidth = $(this.containerRef).width();
          if (this.lastMeasuredWidth !== currentWidth) {
            this.lastMeasuredWidth = currentWidth;
            this.forceUpdate();
          }
        }
      });
    }
  }

  shouldComponentUpdate(nextProps) {
    return !hasEqualProps(this.props, nextProps, [
      'gridCompactType',
      'sectionWidth',
      'sectionPaddingStyle',
      'areFilterResultsEmpty',
      'gridPositions',
      'columnCount',
      'tileSpacingStyle',
      'shouldShowGridLines',
      'gridTaxonomy',
      'GridRenderer', // Should this be here?
      'tabSpacing'
    ]);
  }

  render() {
    const { 
      tileSpacingStyle,
      areFilterResultsEmpty,
      gridTaxonomy, 
      gridPositions, 
      columnCount, 
      sectionWidth,
      sectionPaddingStyle,
      isSectionFullWidth,
      subpage, 
      section, 
      GridRenderer, 
      spacingRowsAtBottomCount,
      shouldShowGridLines,
      canFiltersBeApplied,
      isChoosingTarget,
      isChoosingContentItem
    } = this.props;


    if (gridPositions.length === 0 && !areFilterResultsEmpty) {
      return null;
    }

    const areGridSectionFiltersEnabled = (
      get(gridTaxonomy, 'isEnabled', false) &&
      !isEmpty(gridTaxonomy.categories)
    );

    // Calculate the actual width from the container element if available,
    // otherwise fall back to the sectionWidth prop. This ensures we get the
    // correct width even when the section is inside a tab with padding.
    const internalGridWidth = this.containerRef ? $(this.containerRef).width() : sectionWidth;
    const tileLength = getTileLength(internalGridWidth, columnCount);
    const tileMargin = getTileMargin(tileLength, isSectionFullWidth, tileSpacingStyle);
    const tilePadding = getTilePadding(tileLength, isSectionFullWidth, tileSpacingStyle);
    const highestRow =  getHighestRow(gridPositions, spacingRowsAtBottomCount);
    const containerHeight = getContainerHeight(tileLength, highestRow);

    const tilesStyle = {
      minHeight: containerHeight,
      ...sectionPaddingStyle
    };
    
    return (
      <Fragment>
        {
          areGridSectionFiltersEnabled &&
            <GridSectionFilters
              section={section}
              canFiltersBeApplied={canFiltersBeApplied}
              gridTaxonomy={gridTaxonomy}
              innerMargin={tilePadding}
              innerWrapperMargin={tileMargin}
              filtersWidth={parseInt((internalGridWidth / (1.0 * columnCount)) * columnCount)}
            />
        }

        <div 
          className={`tiles column-count-${columnCount}`} 
          style={tilesStyle}
          ref={(ref) => { this.containerRef = ref; }}
        >

          {
            shouldShowGridLines && 
              <GridLines {...{
                columnCount: section.columnCount,
                internalGridWidth,
                height: containerHeight,
                tilePadding,
                tileMargin
              }}
              />
          }

          <GridRenderer 
            subpage={subpage}
            section={section}
            gridPositions={gridPositions} 
            width={internalGridWidth} 
            columnCount={columnCount}
            tileLength={tileLength}
            tilePadding={tilePadding}
            tileMargin={tileMargin}
            isChoosingTarget={isChoosingTarget}
            isChoosingContentItem={isChoosingContentItem}
          />
        </div>
      </Fragment>
    );
  }

}

GridSectionDesktopLayout.propTypes = {
  gridPositions: PropTypes.array.isRequired,
  columnCount: PropTypes.number.isRequired,
  GridRenderer: PropTypes.oneOfType([
    PropTypes.instanceOf(Component), 
    PropTypes.func
  ]).isRequired,
  isChoosingTarget: PropTypes.bool,
  isChoosingContentItem: PropTypes.bool,
  onSectionClick: PropTypes.func,
  tabSpacing: PropTypes.shape({
    margin: PropTypes.object,
    padding: PropTypes.object,
    innerPadding: PropTypes.object
  })
};

GridSectionDesktopLayout.defaultProps = {
  spacingRowsAtBottomCount: 0
};

export default GridSectionDesktopLayout;
